// ============================================================================
// LES RÔLES ORGAZONE — ce que chacun peut faire, dit d'après les permissions RÉELLES du serveur.
//
// UTILITÉ : l'écran du personnel et l'assistant de création de compte affichent les mêmes rôles ; une seule
// définition, sinon les deux écrans finissent par décrire différemment le même rôle.
// ⚠️ LES TEXTES SUIVENT `server/src/auth/role-permissions.ts`, PAS LA MAQUETTE : elle prête au Support des gestes
//    (renvoyer une invitation, réinitialiser une 2FA) que le serveur ne lui accorde pas — il ne lit que. Un rôle
//    décrit plus large qu'il n'est fait choisir le mauvais.
// ============================================================================
import type { StaffRow } from './api';

export type StaffRole = StaffRow['role'];

export interface StaffRoleInfo {
  v: StaffRole;
  label: string;
  /** Résumé d'une ligne — la liste déroulante et le choix rapide du rôle. */
  hint: string;
  /** Ce que le rôle recouvre, en une phrase. */
  desc: string;
  can: string[];
  cannot: string[];
  /** La permission technique accordée, montrée en infobulle. */
  scope: string;
}

export const STAFF_ROLES: StaffRoleInfo[] = [
  { v: 'superadmin', label: 'Super-admin', hint: 'tout, y compris les 3 verbes réservés', scope: 'console.*',
    desc: 'Tout le plan de contrôle, y compris les gestes irréversibles. Deux personnes au maximum.',
    can: ['créer et supprimer un client', 'créer un compte', 'supprimer une base', 'copier une DSN'], cannot: [] },
  { v: 'deploy_ops', label: 'Mise en service', hint: 'parc, appareils, migrations, blocage magasin', scope: 'deploy:ops',
    desc: 'Met les magasins en service, gère le parc et les appareils, lance les migrations.',
    can: ['mettre un magasin en service', 'gérer le parc et les appareils', 'lancer les migrations', 'bloquer un magasin'],
    cannot: ['créer ou supprimer un client', 'gérer les comptes'] },
  { v: 'support_ops', label: 'Support', hint: 'lecture seule, partout', scope: 'support:ops',
    desc: 'Lecture partout : le rôle d’astreinte, qui voit tout et ne modifie rien.',
    can: ['tout consulter'], cannot: ['toute modification'] },
  { v: 'billing_ops', label: 'Administratif', hint: 'abonnement, facturation, blocage impayé', scope: 'billing:ops',
    desc: 'Suit les abonnements et les échéances. Ne touche à aucune base.',
    can: ['poser et lever un blocage d’abonnement', 'suivre les abonnements'], cannot: ['accéder aux bases', 'gérer les comptes'] },
];

export const STAFF_ROLE = Object.fromEntries(STAFF_ROLES.map((r) => [r.v, r])) as Record<StaffRole, StaffRoleInfo>;

/**
 * Le rôle affiché au pied du rail (maquette scopeLabel/scopeTip) : le libellé humain + la permission technique en
 * infobulle. Priorité au rôle le plus élevé si cumul.
 * ⚠️ Le libellé est celui de `STAFF_ROLE`, que l'écran du personnel et l'assistant de compte affichent aussi : écrit une
 *    seconde fois, le même rôle porterait deux noms selon l'écran.
 */
export function railScopeOf(roles: string[]): { label: string; tip: string } {
  if (roles.includes('superadmin')) return { label: STAFF_ROLE.superadmin.label, tip: 'console.* (verbes réservés inclus)' };
  if (roles.includes('deploy_ops')) return { label: STAFF_ROLE.deploy_ops.label, tip: 'console.read · provisioning.write · device.write · migration.run · killswitch.store' };
  if (roles.includes('billing_ops')) return { label: STAFF_ROLE.billing_ops.label, tip: 'console.read · billing.write · killswitch.subscription' };
  return { label: STAFF_ROLE.support_ops.label, tip: 'console.read' };
}

/**
 * Les modules qu'on ajoute à un rôle, et les accès délégués aux outils des clients — tels que la maquette les dessine.
 * ⚠️ AUCUNE APPLICATION NE LES LIT ENCORE : le store privé et l'EDI ouvrent l'accès automatiquement en bêta, et le
 *    personnel entre au back-office d'un magasin par le ticket de la Console. L'assistant les montre donc grisés,
 *    « à venir », et n'écrit rien (décision de l'utilisateur) — les poser créerait des accès qui n'ouvrent rien.
 */
export const STAFF_MODULES = [
  { k: 'edi', name: 'EDI', scope: 'edi:admin' },
  { k: 'ref', name: 'Référentiels', scope: 'ref:edit' },
  { k: 'store', name: 'Store privé', scope: 'store:editor' },
] as const;
export const STAFF_DELEGATIONS = [
  { k: 'bo', name: 'Back-office magasin', scope: 'bo:support:*' },
  { k: 'erp', name: 'Sliderz Business', scope: 'erp:support:*' },
  { k: 'store', name: 'Store privé', scope: 'store:support:*' },
] as const;
